'use client'

import { Button } from '@workspace/ui/components/Button'
import {
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
} from '@workspace/ui/components/Dialog'
import { Spinner } from '@workspace/ui/components/Spinner'
import { createStore } from '@xstate/store'
import { useSelector } from '@xstate/store/react'
import { XIcon } from 'lucide-react'
import React from 'react'
import { Heading } from 'react-aria-components'
import { cn } from '@workspace/ui/lib/utils'

interface Action {
    label: React.ReactNode
    onClick?: () => void | Promise<void>
}

interface ConfirmDialogContext {
    /** Indicates whether the confirm dialog is open */
    isOpen: boolean
    /** Data for the confirm dialog, including title, description, and actions */
    data?: {
        /** The title displayed in the confirm dialog */
        title: string
        /** Optional description displayed in the confirm dialog */
        description?: React.ReactNode
        /** Optional action button configuration */
        action?: Action
        /** Optional cancel button configuration */
        cancel?: Action
        /** Optional variant for the action button */
        variant?: 'default' | 'destructive'
        /** Class name for custom styling */
        className?: string
    }
}

const confirmStore = createStore({
    context: {
        isOpen: false,
    } as ConfirmDialogContext,
    on: {
        open: (_, data: ConfirmDialogContext['data']) => ({
            isOpen: true,
            data,
        }),
        close: context => ({ ...context, isOpen: false }),
    },
})

const { open, close } = confirmStore.trigger

const confirm = (data: ConfirmDialogContext['data']) => {
    if (!data) return

    open(data)
}

function ConfirmDialog() {
    const isOpen = useSelector(confirmStore, state => state.context.isOpen)
    const data = useSelector(confirmStore, state => state.context.data)
    const variant = data?.variant || 'default'
    const [isLoading, setIsLoading] = React.useState(false)

    const handleCancel = () => {
        if (isLoading) return
        data?.cancel?.onClick?.()
        close()
    }

    const handleAction = async () => {
        if (isLoading) return
        try {
            setIsLoading(true)
            if (data?.action?.onClick) {
                await data.action.onClick()
            }
            close()
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <DialogOverlay
            isOpen={isOpen}
            onOpenChange={isOpen => {
                if (!isOpen && !isLoading) {
                    close()
                }
            }}
        >
            <DialogContent
                className={cn('md:max-w-[425px]', data?.className)}
                isFullscreenOnMobile={false}
                closeButton={false}
            >
                <div className="flex flex-col justify-between gap-4">
                    <DialogHeader>
                        <div className="flex justify-between items-center">
                            <Heading className="font-bold text-lg">{data?.title || 'Confirm'}</Heading>
                            <Button
                                variant="ghost"
                                size="iconSm"
                                className="h-8 w-8"
                                onPress={handleCancel}
                                isDisabled={isLoading}
                            >
                                <XIcon className="w-4 h-4 cursor-pointer" />
                            </Button>
                        </div>
                        <DialogDescription className="text-secondary-foreground font-medium text-sm">
                            {data?.description || 'Are you sure you want to confirm?'}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            className="h-10 min-w-[100px] text-sm  font-medium py-2.5 px-3 rounded-lg flex-1 sm:flex-none"
                            variant="outline"
                            onClick={handleCancel}
                            isDisabled={isLoading}
                        >
                            {data?.cancel?.label || 'Cancel'}
                        </Button>
                        <Button
                            className="h-10 min-w-[100px] text-sm  font-medium py-2.5 px-3 rounded-lg flex-1 sm:flex-none"
                            onClick={handleAction}
                            isDisabled={isLoading}
                            variant={variant}
                        >
                            {isLoading ? <Spinner className="size-5! text-white" /> : data?.action?.label || 'Confirm'}
                        </Button>
                    </DialogFooter>
                </div>
            </DialogContent>
        </DialogOverlay>
    )
}

export { confirm, ConfirmDialog }
